import { useEffect, useRef, useCallback } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { setAuthTokenGetter } from '../services/gameAPI';
import { setFeaturedCaseTokenGetter } from '../services/featuredCaseAPI';

/**
 * Hook to set up authentication token injection for API calls.
 * Should be called once at the app level.
 *
 * Uses a ref pattern to maintain a stable token getter reference
 * while always calling the latest getToken from Clerk.
 *
 * Also monitors for session expiry and dispatches events when
 * a user's session becomes invalid (e.g., after tab is left open too long).
 */
export const useAuthSetup = () => {
  const { getToken, isLoaded, isSignedIn } = useAuth();

  // Ref to hold the latest getToken function
  const getTokenRef = useRef(getToken);

  // Track previous sign-in state to detect session expiry
  const wasSignedInRef = useRef(false);
  const hasInitializedRef = useRef(false);

  // Keep ref updated with latest getToken
  useEffect(() => {
    getTokenRef.current = getToken;
  }, [getToken]);

  // Stable wrapper that delegates to the ref
  const stableGetToken = useCallback(async () => {
    return getTokenRef.current();
  }, []);

  useEffect(() => {
    if (isLoaded) {
      const tokenGetter = isSignedIn ? stableGetToken : null;
      setAuthTokenGetter(tokenGetter);
      setFeaturedCaseTokenGetter(tokenGetter);
    }
  }, [isLoaded, isSignedIn, stableGetToken]);

  // Detect session expiry: user was signed in but now isn't
  useEffect(() => {
    if (!isLoaded) return;

    // Skip the first render after load to avoid false positives
    if (!hasInitializedRef.current) {
      hasInitializedRef.current = true;
      wasSignedInRef.current = isSignedIn;
      return;
    }

    // Check if user was signed in but session expired
    if (wasSignedInRef.current && !isSignedIn) {
      console.log('Session expired: user was signed in but is no longer authenticated');
      window.dispatchEvent(new CustomEvent('auth-session-expired', {
        detail: {
          message: 'Your session has expired. Please sign in again.',
          reason: 'clerk-session-ended'
        }
      }));
    }

    wasSignedInRef.current = isSignedIn;
  }, [isLoaded, isSignedIn]);

  return { isLoaded, isSignedIn };
};
